import React, { useEffect, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'

import { InfoIcon } from '../../../../lib/icons/info-icon'
import BootstrapModalLayout from '../../../../ui/BootstrapModalLayout'
import { Combobox } from '../../../components/combobox'
import Input from '../../../components/Input'
import Toggle from '../../../components/Toggle'
import Tooltip from '../../../components/Tooltip'
import {
    EarthingSystem,
    LoadBalancerGroup,
    useCompanyGetDeviceByCompanyIdQuery,
    useLoadBalancerGroupCreateMutation,
    CompanyPreview,
} from '../../../redux/api/web-api'
import { notifyError, notifySuccess } from '../../notificationsManager/Notify'
import { useLoadbalancerSchema } from '../load-balancer-group-form-validation'
import { Form, Row, RowItem } from '../loadbalancer-group-form/loadbalancer-group-form.styling'
import { LoadbalancerPhaseSettingsWidget } from '../../../components/loadbalancer-phase-settings-widget'
import { EarthingSchemeLB } from '../../../enums/earthingSchemes'
import { Phases } from '../../../enums/phaseTypes'
import useText from '../../../hooks/useText'

export type LoadbalancerGroupModalProps = {
    className?: ClassName
    style?: React.CSSProperties
    closeModal: () => void
    organisations: CompanyPreview[]
    group: LoadBalancerGroup
}

export const SToggleTextInfo = styled.span`
    display: flex;
    gap: 5px;
`

export const transformLBCurrentInput = {
    input: (value: string | number) => {
        const replacedValue = value && value.toString().replace(/^0+/, '')

        return replacedValue === '' ? '0' : replacedValue
    },
    output: (e: React.ChangeEvent<HTMLInputElement>) => {
        const output = parseInt(e.target.value, 10)
        return isNaN(output) ? 0 : output
    },
}

export const LoadbalancerGroupModal = React.memo(function LoadbalancerGroupModal({
    className,
    style,
    closeModal,
    organisations,
    group,
}: LoadbalancerGroupModalProps): React.ReactElement {
    const text = useText()
    const [earthingScheme, setEarthingScheme] = useState<EarthingSystem>(EarthingSchemeLB.TN)

    const [allowedPhases, setAllowedPhases] = useState<Phases | null | undefined>(Phases.L1L2L3)
    const [company, setCompany] = useState(group?.companyId)
    const [measurementDevice, setMeasurementDevice] = useState(group?.measurementDeviceId)
    const [schukoEnabled, setSchukoEnabled] = useState<boolean>(group?.schukoEnabled ?? false)
    const [phasesValid, setPhasesValid] = useState<boolean>(false)
    const [chargePointsIncluded, setChargePointsIncluded] = useState<boolean>(
        group?.chargePointsIncludedInMeasurement ?? true
    )

    const { data: deviceData } = useCompanyGetDeviceByCompanyIdQuery({ companyId: company }, { skip: !company })

    const [createLoadBalancerGroup] = useLoadBalancerGroupCreateMutation()

    const loadbalancerSchema = useLoadbalancerSchema()

    const {
        register,
        trigger,
        watch,
        handleSubmit,
        control,
        formState: { errors, isValid },
    } = useForm<LoadBalancerGroup>({
        resolver: yupResolver(loadbalancerSchema(measurementDevice ? true : false), {
            abortEarly: false,
        }),

        mode: 'all',
        reValidateMode: 'onBlur',
    })
    const minCurrent = watch('minCurrentPerChargePoint')

    useEffect(() => {
        if (minCurrent) {
            trigger('maxCurrentPerPhase')
            trigger('deviceControlledMaxCurrentPerPhase')
        }
    }, [minCurrent, trigger])

    //Add new LB group
    const doAddLBGroup = async (formData: LoadBalancerGroup) => {
        const tempLoadBalancerGroup = {
            ...group,
            ...formData,
            name: formData?.name?.trim(),
            allowedPhases: allowedPhases,
            schukoEnabled: schukoEnabled,
            earthingSystem: earthingScheme,
            chargePointsIncludedInMeasurement: chargePointsIncluded,
        }
        try {
            await createLoadBalancerGroup({ loadBalancerGroup: tempLoadBalancerGroup }).unwrap()
            closeModal()
            notifySuccess(text('msgLoadbalancerGroupAdded'))
        } catch (error) {
            closeModal()
            notifyError(text('msgLoadbalancerGroupAddedError'))
        }
    }

    const disabledSaveButton = !isValid || !company || !phasesValid

    return (
        <BootstrapModalLayout
            className={cn(className)}
            style={style}
            title={text('lblNewLoadbalancerGroupModal')}
            size="lg"
            actions={[
                {
                    label: text('btnCancel'),
                    callback: () => closeModal(),
                    btn: 'btn-secondary',
                },
                {
                    label: text('btnSave'),
                    type: 'submit',
                    form: 'add-form',
                    disabled: disabledSaveButton,
                    callback: handleSubmit(doAddLBGroup),
                },
            ]}
        >
            <Form name="form" role="form" noValidate>
                <span className="mb-5">
                    <Input
                        id="name"
                        labelText={text('lblName')}
                        required
                        data-testid="name-input"
                        {...register('name')}
                        error={errors.name?.message}
                    ></Input>
                </span>
                <span className="mb-5">
                    <Input
                        {...register('description')}
                        id="description"
                        labelText={text('lblDescription')}
                        data-testid="description-input"
                    ></Input>
                </span>

                <span className="mb-5">
                    <Controller
                        name="companyId"
                        control={control}
                        render={({ field: { onChange } }) => (
                            <Combobox
                                id="companyId"
                                required
                                enableDefaultOption={false}
                                labelText={text('lblOrganisation')}
                                options={organisations?.map((organisation) => ({
                                    value: organisation?.id,
                                    label: organisation?.name || '',
                                }))}
                                value={company}
                                {...register('companyId')}
                                onChange={(option) => {
                                    onChange(option.value)
                                    setCompany(option?.value)
                                }}
                                data-testid="company-input"
                                disabledReason={'lblLoadBalancerHasChargePoints'}
                            />
                        )}
                    />
                </span>
                <span className="mb-5">
                    <Controller
                        name="measurementDeviceId"
                        control={control}
                        render={({ field: { onChange } }) => (
                            <Combobox
                                id="measurementDevice"
                                data-testid="device-input"
                                labelText={text('lblMeasurementDevice')}
                                options={deviceData
                                    ?.filter((device) => !device?.loadBalancerGroupId)
                                    .map((device) => ({
                                        value: device?.id,
                                        label: device?.name || device?.serialNumber,
                                    }))}
                                value={measurementDevice}
                                enableDefaultOption={true}
                                {...register('measurementDeviceId')}
                                onChange={(option) => {
                                    onChange(option.value)
                                    setMeasurementDevice(option.value)
                                }}
                                enableDefaultTextOption={
                                    deviceData && deviceData.length > 0
                                        ? text('lblSelect')
                                        : text('lblmeasurementdeviceNotAvailable')
                                }
                                disabled={!company}
                            />
                        )}
                    />
                </span>
                <span className="mb-5">
                    <LoadbalancerPhaseSettingsWidget
                        earthingScheme={earthingScheme}
                        setEarthingScheme={setEarthingScheme}
                        loadbalancerGroup={group}
                        setAllowedPhases={setAllowedPhases}
                        setPhasesValid={setPhasesValid}
                        classNameComponent="flex gap-5 md:flex-nowrap"
                        classNameSubHeading="gap-3"
                    />
                </span>

                <Row>
                    <RowItem>
                        <Controller
                            name="minCurrentPerChargePoint"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input
                                    required
                                    id="minCurrentPerCharger"
                                    labelText={text('lblMinCurrentPerCharger')}
                                    data-testid="MinCurrentPerCharger-input"
                                    {...register('minCurrentPerChargePoint')}
                                    error={errors.minCurrentPerChargePoint?.message}
                                    type="number"
                                    onChange={(e) => {
                                        const transformedValue = parseInt(e.target.value)
                                        onChange(transformLBCurrentInput.input(transformedValue))
                                    }}
                                    value={value ?? undefined}
                                ></Input>
                            )}
                        />
                    </RowItem>
                    <RowItem>
                        <Controller
                            name="maxCurrentPerPhase"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input
                                    id="installationWizardMaxCurrentPerPhase"
                                    labelText={
                                        measurementDevice
                                            ? text('lblMaxCurrentPerPhaseAGOffline')
                                            : text('lblInstallationWizardMaxCurrentPerPhase')
                                    }
                                    required
                                    data-testid="installationWizardMaxCurrentPerPhase-input"
                                    {...register('maxCurrentPerPhase')}
                                    error={errors.maxCurrentPerPhase?.message}
                                    type="number"
                                    onChange={(e) => {
                                        const transformedValue = parseInt(e.target.value)
                                        onChange(transformLBCurrentInput.input(transformedValue))
                                    }}
                                    value={value ?? undefined}
                                ></Input>
                            )}
                        />
                    </RowItem>
                </Row>
                {measurementDevice && (
                    <Row>
                        <RowItem>
                            <Controller
                                name="deviceControlledMaxCurrentPerPhase"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Input
                                        id="maxCurrentPerPhaseAGOnline"
                                        labelText={text('lblMaxCurrentPerPhaseAGOnline')}
                                        required
                                        data-testid="maxCurrentPerPhaseAGGOnline-input"
                                        {...register('deviceControlledMaxCurrentPerPhase')}
                                        error={errors.deviceControlledMaxCurrentPerPhase?.message}
                                        type="number"
                                        onChange={(e) => {
                                            const transformedValue = parseInt(e.target.value)
                                            onChange(transformLBCurrentInput.input(transformedValue))
                                        }}
                                        value={value ?? undefined}
                                    ></Input>
                                )}
                            />
                        </RowItem>
                        <RowItem>
                            <Controller
                                name="supplyMaxCurrent"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Input
                                        id="supplyMaxCurrentMeasurementDeviceField"
                                        labelText={text('lblSupplyMaxCurrent')}
                                        required
                                        data-testid="supplyMaxCurrentMeasurementDeviceField-input"
                                        {...register('supplyMaxCurrent')}
                                        error={errors.supplyMaxCurrent?.message}
                                        type="number"
                                        onChange={(e) => {
                                            const transformedValue = parseInt(e.target.value)
                                            onChange(transformLBCurrentInput.input(transformedValue))
                                        }}
                                        value={value ?? undefined}
                                    ></Input>
                                )}
                            />
                        </RowItem>
                    </Row>
                )}
                <Row>
                    <SToggleTextInfo>
                        <Controller
                            name="schukoEnabled"
                            control={control}
                            render={({ field: { onChange } }) => (
                                <Toggle
                                    {...register('schukoEnabled')}
                                    size="md"
                                    data-testid="socket-enabled-toggle"
                                    isToggled={schukoEnabled}
                                    toggleFn={() => onChange(setSchukoEnabled((schukoEnabled) => !schukoEnabled))}
                                    toggleText={text('lblSocketEnabled')}
                                    labelPos="right"
                                ></Toggle>
                            )}
                        />
                    </SToggleTextInfo>
                </Row>

                {measurementDevice && (
                    <SToggleTextInfo>
                        <Controller
                            name="chargePointsIncludedInMeasurement"
                            control={control}
                            render={({ field: { onChange } }) => (
                                <Toggle
                                    {...register('chargePointsIncludedInMeasurement')}
                                    size="md"
                                    data-testid="chargepoints-included-toggle"
                                    isToggled={chargePointsIncluded}
                                    toggleFn={() =>
                                        onChange(
                                            setChargePointsIncluded((chargePointsIncluded) => !chargePointsIncluded)
                                        )
                                    }
                                    toggleText={text('lblChargersIncludedInPowerConsumption')}
                                    labelPos="right"
                                ></Toggle>
                            )}
                        />

                        <span className="margin-top-6">
                            <Tooltip content={'lblChargersIncludedInPowerConsumptionInfo'}>
                                <InfoIcon id="chargersincluded-info-icon" size={20} />
                            </Tooltip>
                        </span>
                    </SToggleTextInfo>
                )}
            </Form>
        </BootstrapModalLayout>
    )
})

export default LoadbalancerGroupModal
